/**
 * Snapchat Advertising Adapter (Stories 19.7 + 19.8)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 *
 * Story 19.7 wired OAuth + organisation/ad-account discovery; Story 19.8
 * wires the analytics pipeline: 6 parallel sync stats pipelines with field
 * batching for Snap's hard 50-field cap, plus an async stats reports
 * fallback (CSV plain, GET-with-`?async=true`, 24h URL TTL) for >50-field
 * fetches, creative-/media-level breakdowns, and date ranges that exceed
 * Snap's per-granularity sync caps. Populates `SNAPCHAT_ADS_METRICS` (131
 * entries — 38 static + 75 conversion 3-variants + 3 purchase-value
 * 3-variants + 15 custom-event 3-variants; reconciled from the directive's
 * 175-target per Task 0.3) and `SNAPCHAT_ADS_DIMENSIONS` (16 entries) from
 * `_bmad/bmm/data/integration-metrics/snapchat-ads.md`. Widget presets and
 * fixture data are out of scope — those land in Story 19.9.
 *
 * **File-split rationale (mirrors Pinterest 19.5 + MS Ads 19.2 precedent):**
 *   - snapchat-ads.ts            — adapter + metric/dimension catalogues + orchestration
 *   - snapchat/analytics.ts      — sync stats HTTP + parsing + field batching
 *   - snapchat/reports.ts        — async stats Create→Poll→Download→Parse + CSV
 *   - snapchat/report-builders.ts — field lists + URL composers + tz-aware date encoding
 *   - snapchat/transformers.ts   — Snap snake_case → camelCase + conversion 3-variant preservation
 *
 * ADRs:
 *   - ADR-013 (Adapter Pattern): plain object implementing IntegrationAdapter
 *   - ADR-014 (API Versioning): `snapchatAds` lives in `API_VERSIONS`
 *   - ADR-016 (Aggressive Caching): 1h soft TTL via PLATFORM_CACHE_TTL
 *   - ADR-017 (Retry Logic): all HTTP calls flow through `safeFetch`
 *   - ADR-018 (Rate Limiter): Snap ~10 req/sec/token; our worst case ~16 calls/fetchMetrics
 *   - ADR-020 (Token Lifecycle): 1h aggressive access TTL with inlineRefreshOn401=true; refresh tokens permanent
 *   - ADR-023 (Unified Metrics Interface): canonical schema; transformers normalise Snap-isms
 *     (snake_case, micro-currency, ms-durations, swipe_up_percent ambiguity, _app/_web/total preservation)
 */

import type {
  AccountInfo,
  AdapterContext,
  AdapterErrorCode,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RefreshedTokens,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import {
  fetchSnapchatAdsMe,
  fetchSnapchatAdsOrganizationsWithAdAccounts,
  buildSnapchatHeaders,
} from "./api";
import {
  SNAPCHAT_ADS_API_BASE,
  SNAPCHAT_ADS_DELIVERY_ONLY_DIMENSIONS,
  SNAPCHAT_ADS_MAX_DAY_GRANULARITY_DAYS,
  SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST,
  SNAPCHAT_ADS_MAX_HOUR_GRANULARITY_DAYS,
  chunkFieldsForBatching,
  fetchSnapchatAdsCampaignsList,
  fetchSnapchatAdsSyncStats,
  parseSnapchatAdsSyncResponse,
  type SnapchatAdsReportType,
} from "./analytics";
import {
  SnapchatAdsApiCallError,
  parseSnapchatAdsApiError,
  snapchatAdsErrorTypeToAdapterCode,
} from "./errors";
import {
  SNAPCHAT_ADS_SCOPES,
  SnapchatAdsTokenError,
  refreshSnapchatAdsToken,
} from "./oauth";
import {
  FIELDS_BY_REPORT_TYPE,
  ROUTING_BY_REPORT_TYPE,
  buildAsyncStatsRequest,
  buildSyncStatsRequest,
  dateToSnapTimestamp,
} from "./report-builders";
import {
  createSnapchatAdsAsyncStatsReport,
  downloadSnapchatAdsAsyncReport,
  parseSnapchatAdsCsv,
  pollSnapchatAdsAsyncReportUntilDone,
  MAX_POLL_ATTEMPTS_LARGE,
  type SnapchatAdsAsyncEntity,
} from "./reports";
import { TRANSFORMERS } from "./transformers";
import type { AdapterRefreshContext } from "../oauth-families";

// ============================================================================
// Constants
// ============================================================================

/** API version is exported for parity with other adapters' module-level constants. */
export const SNAPCHAT_ADS_API_VERSION = API_VERSIONS.snapchatAds;

/** Hard cap on a single fetchMetrics call to bound peak memory (mirrors Pinterest 19.5). */
export const MAX_FETCHMETRICS_DAYS = 90;

/** Top-N selection caps for entity-scoped pipelines. */
const TOP_CAMPAIGNS_LIMIT = 20;
const TOP_AD_SQUADS_LIMIT = 20;

/** UUID validator for Snap ad-account IDs. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Soft wall-clock budget for an async pipeline before aborting (Pre-mortem F-E). */
const ASYNC_SOFT_BUDGET_MS = 25_000;

/** Levels that route through async (sync stats only goes down to `ad`). */
const ASYNC_ONLY_LEVELS: ReadonlySet<string> = new Set([
  "creative",
  "media",
]);

/**
 * The 6 sync report types Story 19.8 exercises in parallel via `Promise.all`.
 * Order is documentation-only.
 */
export const SNAPCHAT_ADS_REPORT_TYPES = [
  "overview",
  "campaigns",
  "adSquads",
  "ads",
  "ageGender",
  "countryOs",
] as const;

/**
 * Compile-time guarantee the runtime tuple stays in sync with the
 * `SnapchatAdsReportType` union. If a future story adds a 7th variant to
 * one without the other, TypeScript fails here.
 */
type _ReportTypeContractCheck = [
  (typeof SNAPCHAT_ADS_REPORT_TYPES)[number] extends SnapchatAdsReportType
    ? true
    : false,
  SnapchatAdsReportType extends (typeof SNAPCHAT_ADS_REPORT_TYPES)[number]
    ? true
    : false,
];
const _reportTypeContract: _ReportTypeContractCheck = [true, true];
void _reportTypeContract;

const REPORT_TYPES: ReadonlyArray<SnapchatAdsReportType> =
  SNAPCHAT_ADS_REPORT_TYPES;

/**
 * Per-pipeline error → severity classifier. Identical taxonomy to Pinterest
 * 19.5 + MS Ads 19.2 `FATAL_ERROR_CODES`. Fatal errors abort the entire
 * fetchMetrics; non-fatal errors emit an empty `records` slice + a `warnings`
 * entry.
 */
const FATAL_ERROR_CODES: ReadonlySet<AdapterErrorCode> = new Set([
  "token_expired",
  "token_invalid",
  "permission_denied",
  "api_not_enabled",
  "rate_limited",
  "provider_unavailable",
]);

// ============================================================================
// SNAPCHAT_ADS_METRICS — 175 entries (canonical from snapchat-ads.md)
// ============================================================================

const M = (
  key: string,
  label: string,
  type: MetricDefinition["type"],
  category: string,
  dataType: NonNullable<MetricDefinition["dataType"]>,
  compatibleWidgets: string[],
  apiField: string,
  description?: string,
  deprecated?: boolean
): MetricDefinition => {
  const def: MetricDefinition = {
    key,
    label,
    type,
    category,
    dataType,
    compatibleWidgets,
    apiField,
    ...(description !== undefined ? { description } : {}),
    ...(deprecated ? { deprecated: true } : {}),
  };
  return def;
};

// ----------------------------------------------------------------------------
// Conversion event registry (26 events × 3 variants = 78; +3 value variants
// for "purchases" already collapsed below; +5 custom slots × 3 = 15;
// totals: Delivery 6 + Engagement 3 + Cost-Eff 4 + Video Main 7 + Video Att 9
// + App Installs 9 + 78 conv variants + 15 custom = 131. Adding the 3 value
// variants for purchases (already inside the 78) — actual total derives from
// hand-enumerated list below.
// ----------------------------------------------------------------------------

interface ConversionMeta {
  /** snake_case base in Snap (sans `_app`/`_web` suffix) */
  readonly snake: string;
  /** camelCase base in our catalogue */
  readonly camel: string;
  /** Human-readable base label. */
  readonly label: string;
  /** Whether this event has a `_value` Currency variant. */
  readonly hasValue?: boolean;
  /** True for the conversion_rate metric — Percentage type. */
  readonly isRate?: boolean;
}

const CONVERSION_EVENTS: ReadonlyArray<ConversionMeta> = [
  { snake: "page_views", camel: "pageViews", label: "Page Views" },
  { snake: "view_content", camel: "viewContent", label: "View Content" },
  { snake: "add_cart", camel: "addCart", label: "Add to Cart" },
  { snake: "start_checkout", camel: "startCheckout", label: "Start Checkout" },
  { snake: "add_billing", camel: "addBilling", label: "Add Billing Info" },
  { snake: "purchases", camel: "purchases", label: "Purchases", hasValue: true },
  { snake: "sign_ups", camel: "signUps", label: "Sign Ups" },
  { snake: "login", camel: "logins", label: "Logins" },
  { snake: "save", camel: "saves", label: "Saves" },
  { snake: "searches", camel: "searches", label: "Searches" },
  { snake: "share", camel: "shares", label: "Shares" },
  { snake: "app_opens", camel: "appOpens", label: "App Opens" },
  { snake: "start_trial", camel: "startTrial", label: "Start Trial" },
  { snake: "subscribe", camel: "subscribe", label: "Subscribe" },
  { snake: "add_to_wishlist", camel: "addToWishlist", label: "Add to Wishlist" },
  { snake: "list_view", camel: "listView", label: "List View" },
  { snake: "reserve", camel: "reserve", label: "Reserve" },
  { snake: "level_completes", camel: "levelCompletes", label: "Level Completes" },
  { snake: "complete_tutorial", camel: "completeTutorial", label: "Complete Tutorial" },
  { snake: "achievement_unlocked", camel: "achievementUnlocked", label: "Achievement Unlocked" },
  { snake: "spend_credits", camel: "spendCredits", label: "Spend Credits" },
  { snake: "invite", camel: "invite", label: "Invite" },
  { snake: "ad_click", camel: "adClick", label: "Ad Click (Snap)" },
  { snake: "ad_view", camel: "adView", label: "Ad View (Snap)" },
  { snake: "rate", camel: "rate", label: "Conversion Rate", isRate: true },
];

const APP_WEB_DESC =
  "Channel split: total includes both app and web; `_app` is in-app only; `_web` is website only. Values are advertiser-currency converted from Snap's micro-currency (1.00 = 1,000,000 micros).";
const VALUE_DESC =
  "Returned in advertiser currency (Snap returns micro-currency, divided by 1,000,000 client-side). REQUIRES Conversions API v3 with the `contents` array — without it, this field returns 0. Document this when onboarding agencies.";
const VALUE_SUFFIX_DESC = (channel: "app" | "web"): string =>
  `Channel-specific (${channel}-attributed) value. ${VALUE_DESC}`;

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

function buildConversionMetricEntries(): MetricDefinition[] {
  const entries: MetricDefinition[] = [];
  for (const ev of CONVERSION_EVENTS) {
    const baseKey = `conversion${capitalize(ev.camel)}`;
    const baseField = `conversion_${ev.snake}`;
    const baseLabel = ev.isRate ? `Conversion ${ev.label}` : ev.label;
    const widgets = ev.isRate
      ? ["kpi", "gauge"]
      : ev.snake === "purchases"
        ? ["kpi", "line-chart"]
        : ["kpi"];

    if (ev.isRate) {
      entries.push(
        M(
          baseKey,
          `${baseLabel} (Total)`,
          "percentage",
          "conversion_rates",
          "Percentage",
          widgets,
          baseField,
          "Conversion rate over the channel-blind total. Normalised to a fraction in [0, 1]."
        ),
        M(
          `${baseKey}App`,
          `${baseLabel} (App)`,
          "percentage",
          "conversion_rates",
          "Percentage",
          widgets,
          `${baseField}_app`,
          "App-attributed conversion rate."
        ),
        M(
          `${baseKey}Web`,
          `${baseLabel} (Web)`,
          "percentage",
          "conversion_rates",
          "Percentage",
          widgets,
          `${baseField}_web`,
          "Web-attributed conversion rate."
        )
      );
      continue;
    }

    entries.push(
      M(
        baseKey,
        `${baseLabel} (Total)`,
        "number",
        "conversion_total",
        "Integer",
        widgets,
        baseField,
        APP_WEB_DESC
      ),
      M(
        `${baseKey}App`,
        `${baseLabel} (App)`,
        "number",
        "conversion_app",
        "Integer",
        widgets,
        `${baseField}_app`,
        APP_WEB_DESC
      ),
      M(
        `${baseKey}Web`,
        `${baseLabel} (Web)`,
        "number",
        "conversion_web",
        "Integer",
        widgets,
        `${baseField}_web`,
        APP_WEB_DESC
      )
    );

    if (ev.hasValue) {
      entries.push(
        M(
          `${baseKey}Value`,
          `${baseLabel} Value (Total)`,
          "currency",
          "conversion_total",
          "Currency",
          ["kpi", "line-chart"],
          `${baseField}_value`,
          VALUE_DESC
        ),
        M(
          `${baseKey}ValueApp`,
          `${baseLabel} Value (App)`,
          "currency",
          "conversion_app",
          "Currency",
          ["kpi", "line-chart"],
          `${baseField}_value_app`,
          VALUE_SUFFIX_DESC("app")
        ),
        M(
          `${baseKey}ValueWeb`,
          `${baseLabel} Value (Web)`,
          "currency",
          "conversion_web",
          "Currency",
          ["kpi", "line-chart"],
          `${baseField}_value_web`,
          VALUE_SUFFIX_DESC("web")
        )
      );
    }
  }
  return entries;
}

function buildCustomEventMetricEntries(): MetricDefinition[] {
  const entries: MetricDefinition[] = [];
  const desc =
    "Account-specific custom event slot — display name configurable in Snap Business Manager. Adapter exposes the rename via AdapterMetrics.metadata.customEventLabels when Snap returns it.";
  for (const slot of [1, 2, 3, 4, 5] as const) {
    entries.push(
      M(
        `customEvent${slot}`,
        `Custom Event ${slot} (Total)`,
        "number",
        "custom_events",
        "Integer",
        ["kpi"],
        `custom_event_${slot}`,
        desc
      ),
      M(
        `customEvent${slot}App`,
        `Custom Event ${slot} (App)`,
        "number",
        "custom_events",
        "Integer",
        ["kpi"],
        `custom_event_${slot}_app`,
        desc
      ),
      M(
        `customEvent${slot}Web`,
        `Custom Event ${slot} (Web)`,
        "number",
        "custom_events",
        "Integer",
        ["kpi"],
        `custom_event_${slot}_web`,
        desc
      )
    );
  }
  return entries;
}

const STATIC_METRICS: MetricDefinition[] = [
  // ---------- Delivery (6) ----------
  M(
    "impressions",
    "Impressions",
    "number",
    "delivery",
    "Integer",
    ["kpi", "line-chart", "bar-chart"],
    "impressions",
    "Direct impressions of the promoted ad."
  ),
  M(
    "uniques",
    "Unique Users (Reach)",
    "number",
    "delivery",
    "Integer",
    ["kpi"],
    "uniques",
    "Distinct users who saw any impression. Reach & frequency metrics are campaign-level + daily granularity ONLY per Snap docs."
  ),
  M(
    "frequency",
    "Frequency",
    "number",
    "delivery",
    "Decimal",
    ["kpi", "gauge"],
    "frequency",
    "Average impressions per unique user."
  ),
  M(
    "screenTimeMillis",
    "Screen Time (ms)",
    "duration",
    "delivery",
    "Duration",
    ["kpi"],
    "screen_time_millis",
    "Total screen time in milliseconds. Widget formatters convert ms → seconds for display."
  ),
  M(
    "avgScreenTimeMillis",
    "Avg Screen Time (ms)",
    "duration",
    "delivery",
    "Duration",
    ["kpi"],
    "avg_screen_time_millis",
    "Average screen time per impression in milliseconds."
  ),
  M(
    "spend",
    "Spend",
    "currency",
    "delivery",
    "Currency",
    ["kpi", "line-chart", "bar-chart"],
    "spend",
    "Spend in advertiser currency (transformer divides Snap's micro-currency by 1,000,000). Sign-preserving — refunds may appear as negative."
  ),

  // ---------- Engagement (3) ----------
  M(
    "swipeUps",
    "Swipe-Ups",
    "number",
    "engagement",
    "Integer",
    ["kpi", "line-chart", "bar-chart"],
    "swipes",
    "Snap's term for the click equivalent — user swiped up on the ad."
  ),
  M(
    "swipeUpRate",
    "Swipe-Up Rate",
    "percentage",
    "engagement",
    "Percentage",
    ["kpi", "gauge"],
    "swipe_up_percent",
    "Swipes ÷ impressions. Defensively normalised to a fraction in [0, 1] in case Snap returns display-percent form."
  ),
  M(
    "viewTimeMillis",
    "View Time (ms)",
    "duration",
    "engagement",
    "Duration",
    ["kpi"],
    "view_time_millis",
    "Total view time across all impressions in milliseconds."
  ),

  // ---------- Cost Efficiency (4 — DERIVED, not API fields) ----------
  M(
    "ecpm",
    "eCPM",
    "currency",
    "cost_efficiency",
    "Currency",
    ["kpi"],
    "_derived_ecpm",
    "Effective cost per 1,000 impressions. Calculated client-side from spend/impressions — NOT a Snap API field."
  ),
  M(
    "ecpsu",
    "eCPSU (Cost Per Swipe-Up)",
    "currency",
    "cost_efficiency",
    "Currency",
    ["kpi"],
    "_derived_ecpsu",
    "Effective cost per swipe-up. Calculated client-side from spend/swipes — NOT a Snap API field."
  ),
  M(
    "costPerInstall",
    "Cost Per Install",
    "currency",
    "cost_efficiency",
    "Currency",
    ["kpi"],
    "_derived_cost_per_install",
    "Calculated client-side from spend/total_installs — NOT a Snap API field."
  ),
  M(
    "roas",
    "ROAS",
    "number",
    "cost_efficiency",
    "Ratio",
    ["kpi", "gauge"],
    "_derived_roas",
    "Return on ad spend. Calculated client-side from conversion_purchases_value/spend — NOT a Snap API field. Returns 0 when spend is zero or negligible."
  ),

  // ---------- Video — Main Ad (7) ----------
  M(
    "videoViews2s",
    "Video Views (2s)",
    "number",
    "video_main",
    "Integer",
    ["kpi", "line-chart"],
    "video_views",
    "Video views meeting the 2-second threshold."
  ),
  M(
    "videoViews15s",
    "Video Views (15s)",
    "number",
    "video_main",
    "Integer",
    ["kpi"],
    "video_views_15s",
    "Video views meeting the 15-second threshold."
  ),
  M(
    "videoViewsTimeBased",
    "Video Views (Time-Based)",
    "number",
    "video_main",
    "Integer",
    ["kpi"],
    "video_views_time_based"
  ),
  M(
    "viewCompletion",
    "View Completion (100%)",
    "number",
    "video_main",
    "Integer",
    ["kpi"],
    "view_completion",
    "Number of impressions where the user watched 100% of the video."
  ),
  M(
    "videoQuartile1",
    "Video Quartile 25%",
    "number",
    "video_main",
    "Integer",
    ["kpi", "line-chart"],
    "quartile_1"
  ),
  M(
    "videoQuartile2",
    "Video Quartile 50%",
    "number",
    "video_main",
    "Integer",
    ["kpi", "line-chart"],
    "quartile_2"
  ),
  M(
    "videoQuartile3",
    "Video Quartile 75%",
    "number",
    "video_main",
    "Integer",
    ["kpi", "line-chart"],
    "quartile_3"
  ),

  // ---------- Video — Attachment / Long-form (9) ----------
  M(
    "attachmentImpressions",
    "Attachment Impressions",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_impressions"
  ),
  M(
    "attachmentUniques",
    "Attachment Uniques",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_uniques"
  ),
  M(
    "attachmentFrequency",
    "Attachment Frequency",
    "number",
    "video_attachment",
    "Decimal",
    ["kpi"],
    "attachment_frequency"
  ),
  M(
    "attachmentAvgViewTimeMillis",
    "Attachment Avg View Time (ms)",
    "duration",
    "video_attachment",
    "Duration",
    ["kpi"],
    "attachment_avg_view_time_millis",
    "Values are in milliseconds (NOT seconds) — widget formatters handle the display unit."
  ),
  M(
    "attachmentTotalViewTimeMillis",
    "Attachment Total View Time (ms)",
    "duration",
    "video_attachment",
    "Duration",
    ["kpi"],
    "attachment_total_view_time_millis",
    "Values are in milliseconds (NOT seconds)."
  ),
  M(
    "attachmentViewCompletion",
    "Attachment View Completion",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_view_completion"
  ),
  M(
    "attachmentQuartile1",
    "Attachment Quartile 25%",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_quartile_1"
  ),
  M(
    "attachmentQuartile2",
    "Attachment Quartile 50%",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_quartile_2"
  ),
  M(
    "attachmentQuartile3",
    "Attachment Quartile 75%",
    "number",
    "video_attachment",
    "Integer",
    ["kpi"],
    "attachment_quartile_3"
  ),

  // ---------- App Installs (9) ----------
  M(
    "totalInstalls",
    "Total Installs",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "total_installs",
    "Channel-blind sum of app + web installs."
  ),
  M(
    "totalInstallsApp",
    "Total Installs (App)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "total_installs_app"
  ),
  M(
    "totalInstallsWeb",
    "Total Installs (Web)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "total_installs_web"
  ),
  M(
    "iosInstalls",
    "iOS Installs",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "ios_installs"
  ),
  M(
    "iosInstallsApp",
    "iOS Installs (App)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "ios_installs_app"
  ),
  M(
    "iosInstallsWeb",
    "iOS Installs (Web)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "ios_installs_web"
  ),
  M(
    "androidInstalls",
    "Android Installs",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "android_installs"
  ),
  M(
    "androidInstallsApp",
    "Android Installs (App)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "android_installs_app"
  ),
  M(
    "androidInstallsWeb",
    "Android Installs (Web)",
    "number",
    "app_installs",
    "Integer",
    ["kpi"],
    "android_installs_web"
  ),
];

const SNAPCHAT_ADS_METRICS_BASE: MetricDefinition[] = [
  ...STATIC_METRICS,
  ...buildConversionMetricEntries(),
  ...buildCustomEventMetricEntries(),
];

// ============================================================================
// Story 31-31 — aggregation annotations (multi-attachment widgets, ADR-0004)
// ============================================================================
// The positional `M(...)` builder cannot carry the new optional fields without
// churning every call signature, so annotations are composed AFTER the base
// catalogue (sanctioned by Story 31-31 AC #4: "extend the M(...) helper or
// compose after"). Every metric key MUST appear below — omissions fail the
// registry-wide gap report in `metric-registry.aggregation.test.ts`.
//   A  = additive (values sum across attached resources)
//   R  = ratio with the natural weight as denominator (weighted average)
type AggregationAnnotation = Pick<
  MetricDefinition,
  "aggregationKind" | "ratioComponents"
>;
const A: AggregationAnnotation = { aggregationKind: "additive" };
const R = (denominator: string, numerator?: string): AggregationAnnotation => ({
  aggregationKind: "ratio",
  ratioComponents: {
    ...(numerator !== undefined ? { numerator } : {}),
    denominator,
  },
});

const SNAPCHAT_ADS_METRICS_AGGREGATION: Readonly<Record<string, AggregationAnnotation>> = {
  impressions: A,
  uniques: A,
  frequency: R("uniques", "impressions"),
  screenTimeMillis: A,
  avgScreenTimeMillis: R("impressions", "screenTimeMillis"),
  spend: A,
  swipeUps: A,
  swipeUpRate: R("impressions", "swipeUps"),
  viewTimeMillis: A,
  ecpm: R("impressions", "spend"),
  ecpsu: R("swipeUps", "spend"),
  costPerInstall: R("totalInstalls", "spend"),
  roas: R("spend", "conversionPurchasesValue"),
  videoViews2s: A,
  videoViews15s: A,
  videoViewsTimeBased: A,
  viewCompletion: R("videoViews2s"),
  videoQuartile1: A,
  videoQuartile2: A,
  videoQuartile3: A,
  attachmentImpressions: A,
  attachmentUniques: A,
  attachmentFrequency: R("attachmentUniques", "attachmentImpressions"),
  attachmentAvgViewTimeMillis: R("attachmentImpressions", "attachmentTotalViewTimeMillis"),
  attachmentTotalViewTimeMillis: A,
  attachmentViewCompletion: R("attachmentImpressions"),
  attachmentQuartile1: A,
  attachmentQuartile2: A,
  attachmentQuartile3: A,
  totalInstalls: A,
  totalInstallsApp: A,
  totalInstallsWeb: A,
  iosInstalls: A,
  iosInstallsApp: A,
  iosInstallsWeb: A,
  androidInstalls: A,
  androidInstallsApp: A,
  androidInstallsWeb: A,
  conversionPageViews: A,
  conversionPageViewsApp: A,
  conversionPageViewsWeb: A,
  conversionViewContent: A,
  conversionViewContentApp: A,
  conversionViewContentWeb: A,
  conversionAddCart: A,
  conversionAddCartApp: A,
  conversionAddCartWeb: A,
  conversionStartCheckout: A,
  conversionStartCheckoutApp: A,
  conversionStartCheckoutWeb: A,
  conversionAddBilling: A,
  conversionAddBillingApp: A,
  conversionAddBillingWeb: A,
  conversionPurchases: A,
  conversionPurchasesApp: A,
  conversionPurchasesWeb: A,
  conversionPurchasesValue: A,
  conversionPurchasesValueApp: A,
  conversionPurchasesValueWeb: A,
  conversionSignUps: A,
  conversionSignUpsApp: A,
  conversionSignUpsWeb: A,
  conversionLogins: A,
  conversionLoginsApp: A,
  conversionLoginsWeb: A,
  conversionSaves: A,
  conversionSavesApp: A,
  conversionSavesWeb: A,
  conversionSearches: A,
  conversionSearchesApp: A,
  conversionSearchesWeb: A,
  conversionShares: A,
  conversionSharesApp: A,
  conversionSharesWeb: A,
  conversionAppOpens: A,
  conversionAppOpensApp: A,
  conversionAppOpensWeb: A,
  conversionStartTrial: A,
  conversionStartTrialApp: A,
  conversionStartTrialWeb: A,
  conversionSubscribe: A,
  conversionSubscribeApp: A,
  conversionSubscribeWeb: A,
  conversionAddToWishlist: A,
  conversionAddToWishlistApp: A,
  conversionAddToWishlistWeb: A,
  conversionListView: A,
  conversionListViewApp: A,
  conversionListViewWeb: A,
  conversionReserve: A,
  conversionReserveApp: A,
  conversionReserveWeb: A,
  conversionLevelCompletes: A,
  conversionLevelCompletesApp: A,
  conversionLevelCompletesWeb: A,
  conversionCompleteTutorial: A,
  conversionCompleteTutorialApp: A,
  conversionCompleteTutorialWeb: A,
  conversionAchievementUnlocked: A,
  conversionAchievementUnlockedApp: A,
  conversionAchievementUnlockedWeb: A,
  conversionSpendCredits: A,
  conversionSpendCreditsApp: A,
  conversionSpendCreditsWeb: A,
  conversionInvite: A,
  conversionInviteApp: A,
  conversionInviteWeb: A,
  conversionAdClick: A,
  conversionAdClickApp: A,
  conversionAdClickWeb: A,
  conversionAdView: A,
  conversionAdViewApp: A,
  conversionAdViewWeb: A,
  conversionRate: R("impressions"),
  conversionRateApp: R("impressions"),
  conversionRateWeb: R("impressions"),
  customEvent1: A,
  customEvent1App: A,
  customEvent1Web: A,
  customEvent2: A,
  customEvent2App: A,
  customEvent2Web: A,
  customEvent3: A,
  customEvent3App: A,
  customEvent3Web: A,
  customEvent4: A,
  customEvent4App: A,
  customEvent4Web: A,
  customEvent5: A,
  customEvent5App: A,
  customEvent5Web: A,
};

export const SNAPCHAT_ADS_METRICS: MetricDefinition[] = SNAPCHAT_ADS_METRICS_BASE.map((m) => {
  const agg = SNAPCHAT_ADS_METRICS_AGGREGATION[m.key];
  return agg ? { ...m, ...agg } : m;
});


// ============================================================================
// SNAPCHAT_ADS_DIMENSIONS — 16 entries
// ============================================================================

const D = (
  key: string,
  label: string,
  type: DimensionDefinition["type"],
  exampleValues?: string[],
  deliveryOnlyWarning?: boolean
): DimensionDefinition => {
  const def: DimensionDefinition = {
    key,
    label,
    type,
    ...(exampleValues !== undefined ? { exampleValues } : {}),
    ...(deliveryOnlyWarning ? { deliveryOnlyWarning: true } : {}),
  };
  return def;
};

export const SNAPCHAT_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Structure (4)
  D("adAccount", "Ad Account", "Structure"),
  D("campaign", "Campaign", "Structure"),
  D("adSquad", "Ad Squad", "Structure"),
  D("ad", "Ad", "Structure"),
  // Time (3)
  D("granularityHour", "Hour", "Time", ["2026-04-01T00:00:00Z"]),
  D("granularityDay", "Day", "Time", ["2026-04-01"]),
  D("granularityLifetime", "Lifetime", "Time"),
  // Demographics (3)
  D("ageBucket", "Age Bucket", "Demo", [
    "13-17",
    "18-24",
    "25-34",
    "35-49",
    "50+",
    "55+",
  ]),
  D("gender", "Gender", "Demo", ["MALE", "FEMALE", "OTHER"]),
  D("ageGender", "Age + Gender (combined)", "Demo"),
  // Geography (3)
  D("country", "Country", "Geo", ["US", "GB", "CA"]),
  D("region", "Region", "Geo", undefined, true),
  D("dma", "DMA", "Geo", undefined, true),
  // Technology (3)
  D("make", "Device Make", "Tech", ["Apple", "Samsung"], true),
  D("os", "OS", "Tech", ["iOS", "Android"]),
  D("countryOs", "Country + OS (combined)", "Tech"),
];

// ============================================================================
// Pipeline result discriminated union (shared by sync + async pipelines)
// ============================================================================

type PipelineSuccess = {
  ok: true;
  reportType: SnapchatAdsReportType;
  records: Array<Record<string, unknown>>;
};
type PipelineWarning = {
  ok: false;
  fatal: false;
  reportType: SnapchatAdsReportType;
  reason: string;
};
type PipelineFatal = {
  ok: false;
  fatal: true;
  reportType: SnapchatAdsReportType;
  code: AdapterErrorCode;
  message: string;
  retryable: boolean;
  retryAfterMs?: number;
};
type PipelineResult = PipelineSuccess | PipelineWarning | PipelineFatal;

// ============================================================================
// Field-name allowlist (Round 3 — Red Team Attack 5)
// ============================================================================

const ALLOWED_API_FIELDS: ReadonlySet<string> = new Set(
  SNAPCHAT_ADS_METRICS.map((m) => m.apiField).filter(
    (f): f is string => typeof f === "string" && !f.startsWith("_derived_")
  )
);

function validateFieldList(fields: ReadonlyArray<string>): void {
  for (const f of fields) {
    const valid = typeof f === "string" && f.length > 0 && ALLOWED_API_FIELDS.has(f);
    if (valid) continue;
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        // Field name itself is REDACTED to prevent error-log exfiltration
        // of the user-supplied (possibly malicious) input.
        message: "Snapchat Ads field not in allowlist: <redacted-field>",
        status: null,
        retryable: false,
      },
      "Snapchat Ads field-allowlist"
    );
  }
}

// ============================================================================
// Adapter
// ============================================================================

export const snapchatAdsAdapter: IntegrationAdapter = {
  platform: "snapchat-ads",

  rateLimits: {
    // Snap docs cite ~20 req/sec app-wide and ~10 req/sec per token.
    // 60 req/min is a conservative client-side guard.
    requestsPerMinute: 60,
    retryAfterMs: 1000,
  },

  /**
   * Story 19.8 implementation — 6 parallel sync pipelines (with batched ≤50-
   * field requests) + async-on-overflow fallback. See dev notes in
   * `19-8-snapchat-ads-adapter.md` for the complete decision tree.
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // -------- 1. Fixture mode short-circuit (Story 19.9 future, AC #14) ----
    const fixtures =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixtures) {
      return loadSnapchatAdsFixture(ctx, dateRange);
    }

    // -------- 2. Account ID parse (AC #1) -----------------------------------
    const accountId = ctx.accountId ?? "";
    if (!UUID_REGEX.test(accountId)) {
      return adapterErr({
        code: "account_not_found",
        message: "Snapchat Ads account ID must be a UUID string",
        retryable: false,
      });
    }

    // -------- 3. Date range validation (AC #1, Pre-mortem F2) ---------------
    if (
      !(dateRange.start instanceof Date) ||
      !(dateRange.end instanceof Date)
    ) {
      return adapterErr({
        code: "invalid_response",
        message:
          "Snapchat Ads dateRange.start and dateRange.end must be Date objects",
        retryable: false,
      });
    }
    if (
      Number.isNaN(dateRange.start.getTime()) ||
      Number.isNaN(dateRange.end.getTime())
    ) {
      return adapterErr({
        code: "invalid_response",
        message: "Snapchat Ads dateRange contains invalid Date values",
        retryable: false,
      });
    }
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_response",
        message:
          "Snapchat Ads dateRange.start must be on or before dateRange.end",
        retryable: false,
      });
    }
    const startMidnightUtc = Date.UTC(
      dateRange.start.getUTCFullYear(),
      dateRange.start.getUTCMonth(),
      dateRange.start.getUTCDate()
    );
    const endMidnightUtc = Date.UTC(
      dateRange.end.getUTCFullYear(),
      dateRange.end.getUTCMonth(),
      dateRange.end.getUTCDate()
    );
    const days =
      Math.round((endMidnightUtc - startMidnightUtc) / (24 * 60 * 60 * 1000)) +
      1;
    if (days > MAX_FETCHMETRICS_DAYS) {
      return adapterErr({
        code: "invalid_response",
        message: `Snapchat Ads fetchMetrics is capped at ${MAX_FETCHMETRICS_DAYS} days; split larger ranges in the queue consumer`,
        retryable: false,
      });
    }

    // -------- 4. Async-fallback detection (AC #9) --------------------------
    const requestedLevel = ctx.requestedLevel;
    const requestedColumns = ctx.requestedColumns;
    const asyncFallback =
      (requestedLevel !== undefined &&
        ASYNC_ONLY_LEVELS.has(requestedLevel)) ||
      (requestedColumns !== undefined &&
        requestedColumns.length > SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST) ||
      days > SNAPCHAT_ADS_MAX_DAY_GRANULARITY_DAYS;

    if (requestedColumns !== undefined) {
      try {
        validateFieldList(requestedColumns);
      } catch (err) {
        return adapterErr({
          code: "invalid_response",
          message:
            err instanceof Error
              ? sanitizeErrorMessage(err.message)
              : "Snapchat Ads field not in allowlist: <redacted-field>",
          retryable: false,
        });
      }
    }

    // -------- 5. Pre-fetch ad-account metadata (AC #8) ---------------------
    let accountMeta: AdAccountMetadata;
    try {
      accountMeta = await fetchAdAccountMetadata(
        credentials.accessToken,
        accountId
      );
    } catch (err) {
      const code = adapterErrorCodeFromError(err);
      if (FATAL_ERROR_CODES.has(code)) {
        return adapterErr({
          code,
          message: sanitizeErrorMessage(
            `Snapchat Ads ad-account metadata fetch failed: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable:
            code === "rate_limited" || code === "provider_unavailable",
        });
      }
      accountMeta = {
        currency: "",
        timezone: "",
        customEventLabels: null,
      };
    }

    // -------- 6. Pre-fetch top-N campaign IDs (AC #10) ---------------------
    let topCampaignIds: string[] = [];
    let campaignsPartial = false;
    try {
      const result = await fetchSnapchatAdsCampaignsList(
        credentials.accessToken,
        accountId
      );
      // Sort lex-asc by UUID for deterministic top-N selection (no spend
      // ranking pre-fetch — campaigns breakdown returns spend in the same
      // request, so we top-N at orchestrator time after the campaign
      // pipeline runs once).
      topCampaignIds = [...result.campaigns]
        .sort((a, b) =>
          a.id.localeCompare(b.id, undefined, { sensitivity: "base" })
        )
        .slice(0, TOP_CAMPAIGNS_LIMIT)
        .map((c) => c.id);
      campaignsPartial = result.partialDiscovery;
    } catch (err) {
      const code = adapterErrorCodeFromError(err);
      if (FATAL_ERROR_CODES.has(code)) {
        return adapterErr({
          code,
          message: sanitizeErrorMessage(
            `Snapchat Ads campaigns list failed: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable:
            code === "rate_limited" || code === "provider_unavailable",
        });
      }
      // Non-fatal — proceed with empty top-N (entity-scoped pipelines will
      // surface warnings).
    }

    // -------- 7. Run 6 pipelines in parallel (AC #3) -----------------------
    const fetchStart = new Date();
    const pipelines: Array<Promise<PipelineResult>> = REPORT_TYPES.map(
      (reportType) => {
        if (asyncFallback && shouldUseAsync(reportType, requestedLevel)) {
          return runAsyncPipeline({
            reportType,
            accessToken: credentials.accessToken,
            accountId,
            dateRange,
            timeZone: accountMeta.timezone,
            requestedColumns,
            requestedLevel,
            topCampaignIds,
          });
        }
        return runSyncPipeline({
          reportType,
          accessToken: credentials.accessToken,
          accountId,
          dateRange,
          timeZone: accountMeta.timezone,
          topCampaignIds,
        });
      }
    );
    const settled = await Promise.all(pipelines);

    // -------- 8. Classify fatal vs warning (AC #4 + Round 1) ---------------
    const allFatals = settled.filter((r): r is PipelineFatal => !r.ok && r.fatal);
    if (allFatals.length > 0) {
      const PRIORITY: AdapterErrorCode[] = [
        "token_expired",
        "token_invalid",
        "permission_denied",
        "api_not_enabled",
        "rate_limited",
        "provider_unavailable",
      ];
      const sorted = [...allFatals].sort(
        (a, b) => PRIORITY.indexOf(a.code) - PRIORITY.indexOf(b.code)
      );
      const fatal = sorted[0]!;
      return adapterErr({
        code: fatal.code,
        message: fatal.message,
        retryable: fatal.retryable,
        retryAfterMs: fatal.retryAfterMs,
      });
    }

    const warnings: Array<{ reportType: string; reason: string }> = [];
    const records: Array<Record<string, unknown>> = [];
    let dataFinalizedThrough: string | undefined;

    for (const r of settled) {
      if (r.ok) {
        // Field-dropout detection (Pre-mortem A): if Snap silently dropped
        // requested fields (HTTP 200 but lower-than-expected populated key
        // count), emit a warning so widget renders aren't misinterpreted as
        // pixel/conversion-tracking misconfig.
        const expectedFields = FIELDS_BY_REPORT_TYPE[r.reportType].length;
        const firstRow = r.records[0];
        if (firstRow !== undefined) {
          // Count business-data keys (exclude meta `_*` and `_reportType`).
          const populatedKeys = Object.keys(firstRow).filter(
            (k) => !k.startsWith("_")
          ).length;
          if (
            expectedFields > 0 &&
            populatedKeys < Math.floor(expectedFields * 0.8)
          ) {
            warnings.push({
              reportType: r.reportType,
              reason: `field-dropout-detected: ${populatedKeys} populated of ${expectedFields} requested`,
            });
          }
        }
        for (const rec of r.records) {
          if (!rec._reportType || typeof rec._reportType !== "string") {
            return adapterErr({
              code: "invalid_response",
              message:
                "Snapchat Ads transformer emitted a record without _reportType",
              retryable: false,
            });
          }
          const finTime = (rec as { _finalizedDataEndTime?: unknown })
            ._finalizedDataEndTime;
          if (typeof finTime === "string") {
            if (!dataFinalizedThrough || finTime < dataFinalizedThrough) {
              dataFinalizedThrough = finTime;
            }
          }
        }
        records.push(...r.records);
      } else if (!r.fatal) {
        warnings.push({ reportType: r.reportType, reason: r.reason });
      }
    }

    // Mixed age-bucket detection (Pre-mortem 10.5h) — surface if both legacy
    // (`50+`) and new (`55+`) bucket forms appear in age-gender pipeline.
    const ageBuckets = new Set<string>();
    for (const rec of records) {
      const t = rec._reportType;
      if (t === "ageGender") {
        const a = rec.ageBucket;
        if (typeof a === "string") ageBuckets.add(a);
      }
    }
    if (ageBuckets.has("50+") && ageBuckets.has("55+")) {
      warnings.push({
        reportType: "ageGender",
        reason:
          "mixed age bucket sets present (legacy 50+ and new 55+) — date range spans the Feb 2025 migration",
      });
    }

    if (campaignsPartial) {
      warnings.push({
        reportType: "campaigns",
        reason:
          "campaigns list pagination cap hit; some campaigns may not be included in adSquads/ads pipelines",
      });
    }

    // -------- 9. Compose AdapterMetrics (AC #5, #8) -----------------------
    const finalizedThroughDefault = computeDefaultDataFinalizedThrough();
    const finalizedThrough = dataFinalizedThrough ?? finalizedThroughDefault;
    const metrics: AdapterMetrics = {
      platform: "snapchat-ads",
      accountId,
      dateRange,
      records,
      fetchedAt: fetchStart,
      recordCount: records.length,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        adAccountId: accountId,
        currencyCode: accountMeta.currency || undefined,
        timeZone: accountMeta.timezone || undefined,
        customEventLabels: accountMeta.customEventLabels,
        customEventLabelsCapturedAt: fetchStart.toISOString(),
        dataFinalizedThrough: finalizedThrough,
      },
    };
    return adapterOk(metrics);
  },

  /**
   * Validates the Snap connection by calling `/v1/me`. Returns healthy on
   * success, or maps the error to the appropriate `AdapterErrorCode`.
   */
  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    try {
      await fetchSnapchatAdsMe(credentials.accessToken);
      return adapterOk(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(
          `Snapchat Ads connection validation failed: ${message}`
        ),
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  async refreshToken(
    ctx: AdapterRefreshContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Snapchat Ads",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.SNAPCHAT_ADS_CLIENT_ID ?? "";
    const clientSecret = env.SNAPCHAT_ADS_CLIENT_SECRET ?? "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing SNAPCHAT_ADS_CLIENT_ID or SNAPCHAT_ADS_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshSnapchatAdsToken(
        credentials.refreshToken,
        clientId,
        clientSecret
      );
      const rotatedRefreshToken =
        typeof tokenResponse.refresh_token === "string" &&
        tokenResponse.refresh_token.length > 0
          ? tokenResponse.refresh_token
          : credentials.refreshToken;

      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: rotatedRefreshToken,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isInvalidGrant =
        err instanceof SnapchatAdsTokenError
          ? err.oauthError === "invalid_grant"
          : /invalid_grant/i.test(message);
      return adapterErr({
        code: isInvalidGrant ? "token_expired" : "token_invalid",
        message: sanitizeErrorMessage(
          `Snapchat Ads token refresh failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  /**
   * Discovers the FULL flattened ad-account catalog across ALL organizations
   * this grant can access (Story 31.14).
   *
   * ## Why a `single-tenant` platform returns a full catalog
   *
   * The registry classifies snapchat-ads as `oauthGrantScope:
   * "single-tenant"` — V1-EFFECTIVE per the Story 31.5 DEVIATION 2 + Story
   * 31.14 demotion decision, with PROMOTION CRITERIA documented on the
   * registry entry (`registry.ts:205`: promote to multi-tenant when 3+
   * paying customers request multiple Snapchat Ads accounts). That
   * classification governs GRANT/auto-attach treatment, NOT discovery
   * truthfulness: this method intentionally returns the full catalog as
   * promotion forward-compat — when the criteria fire, promotion is a
   * registry-field flip + attach-UI work with the catalog already complete,
   * not an adapter rewrite.
   *
   * The deterministic PRIMARY pick does NOT live here: the api helper
   * lex-asc-sorts the flat list and the OAuth callback takes
   * `flatAdAccounts[0]` (`apps/server/src/routes/integrations/
   * snapchat-ads-oauth.ts`). Likewise the zero-account rejection there is
   * connect-UX only — an empty catalog is a VALID success here (31-4
   * contract; a grant that lost all accounts is a true catalog state).
   *
   * `id` is FROZEN as the Snap AD-ACCOUNT UUID — NOT `organization_id`
   * (overriding the stale sprint-status stub): `buildSnapchatAdsCacheKey`
   * keys on `adAccountId`, the OAuth callback persists `providerAccountId`
   * = primary ad-account id, and 31-3 backfill catalog rows derive from it.
   * `organizationId` stays in `metadata`.
   *
   * Residual gap (31.14 pre-mortem D): when discovery truncates
   * (`partialDiscovery=true` from the api helper's caps/page bound),
   * `AdapterResult<AccountInfo[]>` has no catalog-level metadata channel,
   * so the catalog upsert cannot distinguish full from truncated. Accepted
   * for V1 (realistic grants are single-digit; the OAuth route still warns
   * the human at connect time via `&partialDiscoveryCount`). Re-evaluate at
   * promotion review.
   */
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    try {
      const result = await fetchSnapchatAdsOrganizationsWithAdAccounts(
        credentials.accessToken
      );
      // Defensive dedupe by id (ADR-0004 §Anti-Patterns 4): the dual envelope
      // shapes (`{ adaccount: {...} }` vs bare) or an account echoed under
      // two org entries can present the same id twice. First occurrence wins
      // — the helper's flat list is already lex-asc sorted, so the result is
      // deterministic and order-preserving. Dedupe lives HERE (the catalog
      // boundary) rather than in the api helper because the helper's
      // `flatAdAccounts` also feeds the OAuth callback's primary pick, which
      // is outside this story's blast radius. The DB UNIQUE index is the
      // backstop, not the first line of defense.
      const byId = new Map<string, AccountInfo>();
      for (const acct of result.flatAdAccounts) {
        if (byId.has(acct.id)) continue;
        byId.set(acct.id, {
          id: acct.id,
          name: acct.name || `Account ${acct.id}`,
          platform: "snapchat-ads",
          // 31-4 contract: display/consent metadata ONLY, never access
          // control. Single source of truth is SNAPCHAT_ADS_SCOPES
          // (oauth.ts) — spread into a FRESH array per entry so no shared
          // mutable reference flows into the catalog upsert. Emitting the
          // requested scope is truthful: oauth.ts fails loud on silent
          // scope-narrowing at token exchange, so under-scoped tokens never
          // reach discovery.
          scopes: [...SNAPCHAT_ADS_SCOPES],
          metadata: {
            currency: acct.currency,
            timezone: acct.timezone,
            accountType: acct.type,
            status: acct.status,
            roles: acct.roles,
            organizationId: acct.organizationId,
          },
        });
      }
      return adapterOk([...byId.values()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(
          `Snapchat Ads account discovery failed: ${message}`
        ),
        // Mirror validateConnection (Story 31.14 AC #7): rate limits and
        // transient network failures are retryable — the 31-24 cron's
        // backoff owns retry policy, never adapter-side sleeping.
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return SNAPCHAT_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return SNAPCHAT_ADS_DIMENSIONS;
  },
};

// ============================================================================
// Cache key — exported helper, canonical KV cache key (AC #11, Round 1 H2)
// ============================================================================

/**
 * Canonical cache key shape: `snapchat-ads:{tenantId}:{adAccountId}:{startISO}:{endISO}`.
 *
 * **Empty `tenantId` rejection** (Round 1 — Cache Tenant-Isolation): the
 * helper THROWS synchronously rather than silently producing a colliding
 * key like `snapchat-ads::uuid:...`. This prevents a subtle cross-tenant
 * leak via key collision (mirrors Pinterest 19.5 R1 #H2 hardening).
 *
 * `adAccountId` is also rejected when empty — keeps the key schema
 * unambiguous.
 */
export function buildSnapchatAdsCacheKey(
  tenantId: string,
  adAccountId: string,
  dateRange: { start: Date; end: Date }
): string {
  if (typeof tenantId !== "string" || tenantId.length === 0) {
    throw new Error(
      "buildSnapchatAdsCacheKey: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (typeof adAccountId !== "string" || adAccountId.length === 0) {
    throw new Error("buildSnapchatAdsCacheKey: adAccountId is required");
  }
  const startIso = dateRange.start.toISOString().slice(0, 10);
  const endIso = dateRange.end.toISOString().slice(0, 10);
  return `snapchat-ads:${tenantId}:${adAccountId}:${startIso}:${endIso}`;
}

// ============================================================================
// Re-exports
// ============================================================================

export {
  SNAPCHAT_ADS_API_BASE,
  SNAPCHAT_ADS_DELIVERY_ONLY_DIMENSIONS,
  SNAPCHAT_ADS_MAX_DAY_GRANULARITY_DAYS,
  SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST,
  SNAPCHAT_ADS_MAX_HOUR_GRANULARITY_DAYS,
};

// ============================================================================
// Internal: ad-account metadata
// ============================================================================

interface AdAccountMetadata {
  currency: string;
  timezone: string;
  customEventLabels: Record<string, string> | null;
}

interface RawAdAccountMetadataResponse {
  request_status?: string;
  adaccount?: {
    id?: string;
    currency?: string;
    timezone?: string;
    custom_event_labels?: Record<string, unknown>;
  };
}

async function fetchAdAccountMetadata(
  accessToken: string,
  accountId: string
): Promise<AdAccountMetadata> {
  const result = await safeFetch(
    `${SNAPCHAT_ADS_API_BASE}/adaccounts/${encodeURIComponent(accountId)}`,
    { method: "GET", headers: buildSnapchatHeaders(accessToken) },
    15_000
  );
  if (!result.success) {
    throw buildTypedApiError(
      result.error,
      "Snapchat Ads ad-account metadata"
    );
  }
  let json: RawAdAccountMetadataResponse;
  try {
    json = (await result.data.json()) as RawAdAccountMetadataResponse;
  } catch {
    return { currency: "", timezone: "", customEventLabels: null };
  }
  const acct = json.adaccount ?? {};

  let customEventLabels: Record<string, string> | null = null;
  if (
    acct.custom_event_labels &&
    typeof acct.custom_event_labels === "object" &&
    !Array.isArray(acct.custom_event_labels)
  ) {
    const labels: Record<string, string> = {};
    for (const [k, v] of Object.entries(acct.custom_event_labels)) {
      if (typeof v === "string") labels[k] = v;
    }
    if (Object.keys(labels).length > 0) {
      customEventLabels = labels;
    }
  }

  return {
    currency: typeof acct.currency === "string" ? acct.currency : "",
    timezone: typeof acct.timezone === "string" ? acct.timezone : "",
    customEventLabels,
  };
}

function buildTypedApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): SnapchatAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON
    }
  }
  const parsed = parseSnapchatAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new SnapchatAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}

// ============================================================================
// Internal: sync pipeline runner
// ============================================================================

interface SyncPipelineInput {
  reportType: SnapchatAdsReportType;
  accessToken: string;
  accountId: string;
  dateRange: { start: Date; end: Date };
  timeZone: string;
  topCampaignIds: ReadonlyArray<string>;
}

async function runSyncPipeline(
  input: SyncPipelineInput
): Promise<PipelineResult> {
  try {
    const routing = ROUTING_BY_REPORT_TYPE[input.reportType];
    const fields = FIELDS_BY_REPORT_TYPE[input.reportType];

    // Field-allowlist enforcement — Round 3 Red Team Attack 5.
    // Our internal field lists are static and pre-vetted, but we run them
    // through the same allowlist as user-supplied columns for consistency.
    validateFieldList(fields);

    // Note: when routing.reportDimension is a delivery-only dim
    // (region/dma/make), `buildSyncStatsRequest` strips conversion fields
    // at request-build time. The orchestrator records a warning at the
    // outer fetchMetrics layer rather than per-pipeline so callers see
    // a single ad-account-level signal.

    const allRecords: Array<Record<string, unknown>> = [];

    // Entity-fan-out for adSquads (per-campaign) and ads (per-adsquad).
    if (input.reportType === "adSquads") {
      if (input.topCampaignIds.length === 0) {
        return {
          ok: false,
          fatal: false,
          reportType: input.reportType,
          reason: "No campaigns available for adSquads pipeline",
        };
      }
      // Run one stats call per top campaign, merge.
      const subResults = await Promise.all(
        input.topCampaignIds.map(async (campaignId) => {
          return runSinglePipelineFetch({
            ...input,
            entity: "campaign",
            entityId: campaignId,
            fields,
          });
        })
      );
      for (const sub of subResults) {
        allRecords.push(...sub);
      }
      const transformer = TRANSFORMERS[input.reportType];
      const records = transformer(allRecords).slice(0, TOP_AD_SQUADS_LIMIT * 6); // sane upper bound
      return { ok: true, reportType: input.reportType, records };
    }

    if (input.reportType === "ads") {
      // Sync `ads` requires per-ad-squad fan-out: top-N ad-squad IDs are
      // discovered inside the `adSquads` pipeline (which runs in parallel
      // with this one), so we can't read them here without sequencing the
      // pipelines. Story 19.8 explicitly defers full ad-level coverage to
      // 19.9 widget presets — those will either (a) drive a 2-phase fetch
      // (adSquads → ads) or (b) request creative-level breakdowns via
      // ctx.requestedLevel and route this pipeline to the async path.
      // Until then, emit a non-fatal warning so widgets show "no data" copy
      // instead of erroring.
      return {
        ok: false,
        fatal: false,
        reportType: input.reportType,
        reason:
          "Ad-level breakdown deferred to Story 19.9; sync `ads` pipeline requires top-N ad-squad fan-out which is not yet wired",
      };
    }

    const records = await runSinglePipelineFetch({
      ...input,
      entity: routing.entity,
      entityId: input.accountId,
      fields,
    });
    const transformer = TRANSFORMERS[input.reportType];
    return {
      ok: true,
      reportType: input.reportType,
      records: transformer(records),
    };
  } catch (err) {
    return classifyPipelineError(err, input.reportType);
  }
}

interface SinglePipelineFetchInput extends SyncPipelineInput {
  entity: "adaccount" | "campaign" | "adsquad";
  entityId: string;
  fields: ReadonlyArray<string>;
}

async function runSinglePipelineFetch(
  input: SinglePipelineFetchInput
): Promise<Array<Record<string, unknown>>> {
  // Build the request to capture pre-stripped fields + options.
  const built = buildSyncStatsRequest({
    reportType: input.reportType,
    accountId: input.accountId,
    dateRange: input.dateRange,
    timeZone: input.timeZone,
    fieldsOverride: input.fields,
  });

  // Field batching for ≤50-field cap (mostly a no-op for our 6 default
  // pipelines whose lists are pre-pruned to ≤31 fields, but the chunk
  // helper makes the code future-proof for a 78-field conversion fetch
  // requested via `requestedColumns`).
  const batches = chunkFieldsForBatching(
    built.options.fields,
    SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST
  );

  const merged: Map<string, Record<string, unknown>> = new Map();
  // Per-batch row index — used as a deterministic tiebreaker when a row has
  // no dimension/timestamp keys to compose an identity from. Tracked across
  // the whole pipeline (not per-batch) so multi-batch merging stays stable.
  let unidentifiedRowIndex = 0;
  await Promise.all(
    batches.map(async (batchFields) => {
      const opts = { ...built.options, fields: batchFields };
      const env = await fetchSnapchatAdsSyncStats(
        input.accessToken,
        input.entity,
        input.entityId,
        opts
      );
      const rows = parseSnapchatAdsSyncResponse(env, input.reportType);
      for (const row of rows) {
        let id = computeRowIdentity(row);
        if (id === "") {
          id = `unidentified-${unidentifiedRowIndex++}`;
        }
        const existing = merged.get(id);
        if (existing) {
          merged.set(id, { ...existing, ...row });
        } else {
          merged.set(id, row);
        }
      }
    })
  );
  return Array.from(merged.values());
}

function computeRowIdentity(row: Record<string, unknown>): string {
  // Identity = combination of timestamp boundaries + entity + dimension
  // values (whatever's present). Used to merge multi-batch results.
  // Returns empty string when no keys are present so the caller can apply
  // a deterministic fallback (sequential index) instead of `Math.random()`.
  const parts: string[] = [];
  for (const k of [
    "_entityId",
    "start_time",
    "end_time",
    "campaign_id",
    "adsquad_id",
    "ad_id",
    "age",
    "gender",
    "country",
    "os",
  ]) {
    const v = (row as Record<string, unknown>)[k];
    if (v !== undefined && v !== null) parts.push(`${k}=${String(v)}`);
  }
  return parts.length > 0 ? parts.join("|") : "";
}

function shouldUseAsync(
  reportType: SnapchatAdsReportType,
  requestedLevel: string | undefined
): boolean {
  // Async fallback applies to the `ads` pipeline (sync `ads` returns a
  // warning because it requires top-N ad-squad IDs which 19.8 doesn't
  // pre-fetch — see runSyncPipeline `ads` branch) AND to any pipeline when
  // creative-/media-level reporting is requested (Snap sync only goes down
  // to `ad`). When asyncFallback is true, this routes the `ads` pipeline
  // exclusively; other 5 pipelines stay on sync because they don't benefit
  // from async (no creative-level data they could surface).
  if (requestedLevel && ASYNC_ONLY_LEVELS.has(requestedLevel)) {
    return reportType === "ads";
  }
  return reportType === "ads";
}

// ============================================================================
// Internal: async pipeline runner (with stale-URL recovery + budget guard)
// ============================================================================

interface AsyncPipelineInput {
  reportType: SnapchatAdsReportType;
  accessToken: string;
  accountId: string;
  dateRange: { start: Date; end: Date };
  timeZone: string;
  requestedColumns?: ReadonlyArray<string>;
  requestedLevel?: string;
  topCampaignIds: ReadonlyArray<string>;
}

/**
 * Async stats reports flow.
 *
 * **Wall-clock budget warning:** this code path can exceed the 30s
 * Cloudflare Workers HTTP-trigger limit. Invoke ONLY from queue consumers
 * (15min budget) or cron handlers. The runtime budget guard below soft-
 * aborts at 25s to leave headroom — but the right fix is for callers to
 * pass `ctx.invocationContext` (deferred to a follow-up story).
 */
async function runAsyncPipeline(
  input: AsyncPipelineInput
): Promise<PipelineResult> {
  const startMs = Date.now();
  try {
    const fields =
      input.requestedColumns && input.requestedColumns.length > 0
        ? input.requestedColumns
        : FIELDS_BY_REPORT_TYPE[input.reportType];
    validateFieldList(fields);

    // Creative- and media-level breakdowns require an actual entity UUID
    // which we don't pre-fetch in 19.8 (the story scopes that to 19.9
    // widget presets). Routing them through the ad-account-scoped async
    // path lets Snap's report aggregator surface creative-level rows in
    // the breakdown columns rather than 404-ing — same pattern Pinterest
    // 19.5 uses for PIN_PROMOTION-level reports without explicit pin IDs.
    //
    // When 19-9 lands creative-level pre-fetching, replace the default
    // with the actual creative entity + ID before composing the request.
    const entity: SnapchatAdsAsyncEntity = "adaccount";
    const entityId = input.accountId;

    const built = buildAsyncStatsRequest({
      entity,
      entityId,
      dateRange: input.dateRange,
      granularity: "DAY",
      fields,
      timeZone: input.timeZone,
    });

    const created = await createSnapchatAdsAsyncStatsReport(
      input.accessToken,
      built.entity,
      built.entityId,
      {
        startTime: built.startTime,
        endTime: built.endTime,
        granularity: built.granularity,
        fields: built.fields,
        breakdown: built.breakdown,
        reportDimension: built.reportDimension,
        swipeUpAttributionWindow: built.swipeUpAttributionWindow,
        viewAttributionWindow: built.viewAttributionWindow,
        conversionSourceTypes: built.conversionSourceTypes,
        actionReportTime: built.actionReportTime,
      }
    );

    // Soft wall-clock guard (Pre-mortem F-E): async polling can run for
    // minutes; if we've already crossed the 25s soft budget by the time
    // create returns, abort before spending more time on poll/download.
    // The remaining budget after create is checked again after poll.
    if (Date.now() - startMs > ASYNC_SOFT_BUDGET_MS) {
      throw new SnapchatAdsApiCallError(
        {
          type: "rate_limited",
          message:
            "Snapchat Ads async fallback exceeded soft 25s budget — invoke from queue or cron context",
          status: null,
          retryable: true,
        },
        "Snapchat Ads async pipeline"
      );
    }

    const isLargeReport =
      input.requestedLevel === "creative" ||
      input.requestedLevel === "media";
    const pollOpts = isLargeReport
      ? { maxAttempts: MAX_POLL_ATTEMPTS_LARGE }
      : undefined;

    let downloadUrl = await pollSnapchatAdsAsyncReportUntilDone(
      input.accessToken,
      built.entity,
      built.entityId,
      created.report_run_id,
      pollOpts
    );

    if (Date.now() - startMs > ASYNC_SOFT_BUDGET_MS) {
      throw new SnapchatAdsApiCallError(
        {
          type: "rate_limited",
          message:
            "Snapchat Ads async fallback exceeded soft 25s budget after poll — invoke from queue or cron context",
          status: null,
          retryable: true,
        },
        "Snapchat Ads async pipeline"
      );
    }

    let csvBody: string;
    try {
      csvBody = await downloadSnapchatAdsAsyncReport(downloadUrl);
    } catch (err) {
      // Stale download URL recovery: re-poll once for a fresh URL.
      const isStaleUrl =
        err instanceof SnapchatAdsApiCallError &&
        (err.type === "permission_denied" || err.status === 403);
      if (!isStaleUrl) throw err;
      downloadUrl = await pollSnapchatAdsAsyncReportUntilDone(
        input.accessToken,
        built.entity,
        built.entityId,
        created.report_run_id,
        pollOpts
      );
      csvBody = await downloadSnapchatAdsAsyncReport(downloadUrl);
    }

    const rows = parseSnapchatAdsCsv(csvBody);
    const transformer = TRANSFORMERS[input.reportType];
    return {
      ok: true,
      reportType: input.reportType,
      records: transformer(rows),
    };
  } catch (err) {
    return classifyPipelineError(err, input.reportType);
  }
}

// ============================================================================
// Internal: error classification (shared by sync + async)
// ============================================================================

function classifyPipelineError(
  err: unknown,
  reportType: SnapchatAdsReportType
): PipelineResult {
  if (err instanceof SnapchatAdsApiCallError) {
    const code = snapchatAdsErrorTypeToAdapterCode(err.type);
    if (FATAL_ERROR_CODES.has(code)) {
      return {
        ok: false,
        fatal: true,
        reportType,
        code,
        message: sanitizeErrorMessage(
          `Snapchat Ads ${reportType} pipeline failed: ${err.message}`
        ),
        retryable: err.retryable,
      };
    }
    return {
      ok: false,
      fatal: false,
      reportType,
      reason: sanitizeErrorMessage(err.message),
    };
  }
  return {
    ok: false,
    fatal: false,
    reportType,
    reason: sanitizeErrorMessage(
      err instanceof Error ? err.message : String(err)
    ),
  };
}

// ============================================================================
// Fixture-mode helper (lazy-imports the fixtures sub-package)
// ============================================================================

async function loadSnapchatAdsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Snapchat Ads demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve demo client slug",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Snapchat Ads demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "snapchat-ads",
    clientSlug
  );

  if (metrics === null) {
    return adapterErr({
      code: "account_not_found",
      message: `Snapchat Ads demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end
  );
  return adapterOk(filtered);
}

// ============================================================================
// Internal: data-finalisation timestamp (Pre-mortem 8.5)
// ============================================================================

/**
 * Compute the default `dataFinalizedThrough` ISO timestamp: now − 48h per
 * Snap docs ("Metrics are finalized 48 hours after the end of the day in
 * the timezone set by the Ad Account"). Widgets render today/yesterday
 * data with a "preliminary" marker when records' boundaries fall after
 * this timestamp.
 */
function computeDefaultDataFinalizedThrough(): string {
  return new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
}

// ============================================================================
// Internal: error-code inference
// ============================================================================

function adapterErrorCodeFromError(err: unknown): AdapterErrorCode {
  if (err instanceof SnapchatAdsApiCallError && err.type !== "unknown") {
    return snapchatAdsErrorTypeToAdapterCode(err.type);
  }
  // Status-less transport failures (timeout, DNS, connection refused) reach
  // here wrapped as SnapchatAdsApiCallError type "unknown" — the api helpers
  // wrap EVERY safeFetch failure, and parseSnapchatAdsApiError can't classify
  // an error that has no HTTP status. Sniff safeFetch's sanitized message so
  // genuine network errors map to `network_error` (retryable downstream —
  // Story 31.14 AC #7/#9) instead of `invalid_response` (terminal).
  const message = err instanceof Error ? err.message : String(err);
  if (/timed out|Network error|fetch failed/i.test(message)) {
    return "network_error";
  }
  if (err instanceof SnapchatAdsApiCallError) {
    return snapchatAdsErrorTypeToAdapterCode(err.type);
  }
  return "invalid_response";
}

// ============================================================================
// Test-only exports
// ============================================================================

/** Exposed for tests that audit the field-allowlist. */
export const __ALLOWED_API_FIELDS_FOR_TESTS = ALLOWED_API_FIELDS;
/** Exposed for tests that audit `safeRoas` / numeric helpers via the catalogue. */
export { dateToSnapTimestamp };
